import axios from 'axios';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import api from '../../api/api';
import { HUBBLE_APP_SECRET, HUBBLE_CLIENT_ID, HUBBLE_SDK_URL } from '../../constants/config';
import { trackData } from '../../utils/trackers';
import './_embeddedApps.scss';

/**
 * Hosts the Hubble (gift cards) web SDK in an iframe that outlives the screen that shows it.
 *
 * Once the post-login data is in and the user is eligible, Hubble is authenticated and loaded in the
 * background. The Gift Cards screen (pages/Hubble) only renders a slot; the iframe is positioned over that
 * slot while the screen is open and hidden (never unmounted) otherwise, so Hubble's own session check
 * happens once, not on every visit. It is torn down when the user logs out or switches identity.
 */

export type HubbleStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface HubbleState {
  status: HubbleStatus;
  src: string;
  error: string;
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface EmbeddedAppsContextValue {
  hubble: HubbleState;
  register: (el: HTMLElement) => void;
  unregister: (el: HTMLElement) => void;
  reload: () => void;
}

const IDLE: HubbleState = { status: 'idle', src: '', error: '' };
// Let the burst of post-login requests settle before spending a request on Hubble's auth.
const PRELOAD_DELAY_MS = 400;

const EmbeddedAppsContext = createContext<EmbeddedAppsContextValue | null>(null);

const getHubbleToken = (response: any): string | undefined =>
  response?.token || response?.data?.token || response?.sso_token || response?.data?.sso_token;

const getHubbleOrigin = () => {
  try {
    return new URL(HUBBLE_SDK_URL).origin;
  } catch (e) {
    return '';
  }
};

const sameRect = (a: Rect | null, b: Rect) =>
  !!a && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;

export const EmbeddedAppsProvider: React.FC = ({ children }) => {
  const navigate = useNavigate(),
    auth = useSelector((state: any) => state.auth),
    userDetails = useSelector((state: any) => state.data?.userDetails),
    hasGiftCards = useSelector((state: any) => state.data?.hasGiftCards),
    [hubble, setHubbleState] = useState<HubbleState>(IDLE),
    [slotEl, setSlotEl] = useState<HTMLElement | null>(null),
    [rect, setRect] = useState<Rect | null>(null),
    // Mirrors of state for guards that must see the latest value synchronously (e.g. two effects racing to load).
    hubbleRef = useRef(hubble),
    slotElRef = useRef<HTMLElement | null>(null),
    controllerRef = useRef<AbortController | null>(null);

  slotElRef.current = slotEl;

  const identityKey = auth?.isLoggedIn && auth?.member_id ? `${auth.member_id}|${auth.organisation_uuid}` : '',
    // Mirrors the Router: /gift-cards only exists for ACTIVE members in employee mode.
    canPreload = auth?.status === 'ACTIVE' && auth?.activeMode === 'employee' && hasGiftCards === true,
    dataLoaded = !!identityKey && !!userDetails && hasGiftCards !== null;

  const setHubble = useCallback((state: HubbleState) => {
    hubbleRef.current = state;
    setHubbleState(state);
  }, []);

  const load = useCallback(async () => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setHubble({ status: 'loading', src: '', error: '' });

    try {
      const token = getHubbleToken(await api.hubble.createSsoToken(controller.signal));
      if (!token) throw new Error('The Hubble token service returned an invalid response.');
      if (controller.signal.aborted) return;

      const url = new URL(HUBBLE_SDK_URL);
      url.searchParams.set('clientId', HUBBLE_CLIENT_ID);
      url.searchParams.set('appSecret', HUBBLE_APP_SECRET);
      url.searchParams.set('token', token);
      // Stays 'loading' until the SDK's app_ready message arrives.
      setHubble({ status: 'loading', src: url.toString(), error: '' });
    } catch (error: any) {
      if (axios.isCancel(error) || controller.signal.aborted) return;

      setHubble({
        status: 'error',
        src: '',
        error:
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          error?.message ||
          'We could not open Gift Cards right now. Please try again.',
      });
      trackData('Hubble Load Failed', { status: error?.response?.status });
    }
  }, [setHubble]);

  const register = useCallback((el: HTMLElement) => setSlotEl(el), []);
  const unregister = useCallback((el: HTMLElement) => setSlotEl((current) => (current === el ? null : current)), []);

  // Logout or a different user/organisation: drop the iframe and any token that belongs to the old identity.
  useEffect(
    () => () => {
      controllerRef.current?.abort();
      setHubble(IDLE);
    },
    [identityKey, setHubble]
  );

  // Preload in the background once the post-login data has settled.
  useEffect(() => {
    if (!dataLoaded || !canPreload) return;

    const timer = window.setTimeout(() => {
      if (hubbleRef.current.status === 'idle') load();
    }, PRELOAD_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [dataLoaded, canPreload, hubble.status, load]);

  // The screen was opened before the preload ran (deep link, or still within the delay above).
  useEffect(() => {
    if (slotEl && identityKey && hubbleRef.current.status === 'idle') load();
  }, [slotEl, identityKey, hubble.status, load]);

  useEffect(() => {
    const hubbleOrigin = getHubbleOrigin();

    const handleMessage = (event: MessageEvent) => {
      if (!hubbleOrigin || event.origin !== hubbleOrigin || !event.data || typeof event.data !== 'object') return;

      if (event.data.type === 'action') {
        if (event.data.action === 'app_ready') {
          setHubble({ ...hubbleRef.current, status: 'ready' });
          trackData('Hubble Ready', { Preloaded: !slotElRef.current });
        } else if (event.data.action === 'close') {
          trackData('Hubble Closed');
          navigate('/home');
          // The SDK has ended its session; start a fresh one in the background for the next visit.
          load();
        } else if (event.data.action === 'error') {
          setHubble({ status: 'error', src: '', error: 'Hubble could not start your rewards session. Please try again.' });
          trackData('Hubble SDK Error');
        }
      }

      if (event.data.type === 'analytics' && event.data.event) {
        trackData(`Hubble ${event.data.event}`, event.data.properties || {});
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [navigate, load, setHubble]);

  // Follow the screen's slot with the fixed-position iframe.
  useEffect(() => {
    if (!slotEl) {
      setRect(null);
      return;
    }

    const update = () => {
      const { top, left, width, height } = slotEl.getBoundingClientRect();
      const next = { top, left, width, height };
      setRect((current) => (sameRect(current, next) ? current : next));
    };

    update();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    observer?.observe(slotEl);
    // Layout inputs such as the footer height are published after mount, so measure again once they settle.
    const frame = window.requestAnimationFrame(update);
    const timer = window.setTimeout(update, 300);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);

    return () => {
      observer?.disconnect();
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [slotEl]);

  const value = useMemo(() => ({ hubble, register, unregister, reload: load }), [hubble, register, unregister, load]);
  const isShown = !!slotEl && !!rect;

  return (
    <EmbeddedAppsContext.Provider value={value}>
      {children}
      {hubble.src && (
        <iframe
          className={`embedded-app-frame${isShown ? '' : ' is-hidden'}${hubble.status === 'ready' ? ' is-ready' : ''}`}
          style={isShown && rect ? rect : undefined}
          title="Gift Cards"
          src={hubble.src}
          allow="clipboard-write *"
          scrolling="yes"
          aria-hidden={!isShown}
          tabIndex={isShown ? undefined : -1}
        />
      )}
    </EmbeddedAppsContext.Provider>
  );
};

/**
 * For the Gift Cards screen. Attach `slotRef` to the element Hubble should fill; the shared iframe is shown
 * over it while the screen is mounted and hidden again when it unmounts.
 */
export const useHubble = () => {
  const context = useContext(EmbeddedAppsContext);
  if (!context) throw new Error('useHubble must be used inside EmbeddedAppsProvider');

  const { hubble, register, unregister, reload } = context,
    slotRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = slotRef.current;
    if (!el) return;

    register(el);
    return () => unregister(el);
  }, [register, unregister]);

  return { state: hubble, slotRef, reload };
};
